// ================= IMPACT CANVAS =================
//
// The burst at the point of contact. deploy_handoff/impact-grammar-plan.md, Phase E, and the first
// thing in this repo whose correctness is "did it draw" rather than "does it say the right words".
//
// WHY A CANVAS AT ALL, when Phases B-D are pure CSS. Everything up to here moves elements that
// already exist -- the figure, the panel, the pips -- and CSS is the cheaper and safer tool for
// that by a wide margin. A burst is the one effect with no element to move: it is thirty small
// things with independent velocities and lifetimes, and expressing that as thirty absolutely
// positioned divs would put thirty nodes into the busiest screen in the game, every beat, for
// 400ms. One canvas is one node.
//
// IT IS BLANK AT REST AND NOT RUNNING. There is no ambient loop: requestAnimationFrame is started
// by a burst and stops itself when the last particle dies, so a page sitting on a beat schedules
// nothing. The game has exactly one other rAF (js/library-ui.js) and this must not quietly become a
// second permanent one. The canvas is created per panel, and the panel is destroyed and recreated
// every beat, so there is nothing to clean up and no stale surface can survive into the next beat.
//
// REDUCED MOTION IS CHECKED IN JS, not left to the stylesheet, because a canvas draws whatever it
// is told to. Same switch, same meaning: with motion off this file paints nothing at all, which is
// what keeps the rest-state invariant true on this layer and what keeps the 21-check browser tier
// blind to it (runChrome passes --force-prefers-reduced-motion).
//
// WHERE THE BURST LANDS, and the measurement that did NOT work.
//
// js/move-art-figures.js publishes MOVE_ART_GEOMETRY: per pose, the ink box measured off the alpha
// channel by tools/build-move-art-registry.js. The plan expected to take the contact point straight
// out of it -- high for a head kick, low for a sweep. Measured, that is only half true:
//
//   w  0.627 .. 0.920   real spread     rx 0.041 .. 0.242   real spread
//   h  0.584 .. 0.857   FOUR distinct values across 26 poses
//   by 0.084 .. 0.209   TWO distinct values across 26 poses
//
// The published box is the UNION of every fighter's ink for that pose, and every one of these poses
// is a standing figure, so vertically they all span the same band whatever the limb is doing. The
// geometry knows how far a technique REACHES and does not know how high it lands.
//
// So the horizontal origin is derived (1 - rx is the ink's right edge, which is where an extended
// technique actually ends) and the vertical origin is authored below. Authoring 26 numbers is worth
// more than deriving one wrong one, and the poses are a closed set that only changes when an artist
// draws a new one -- at which point move-art-test fails for the missing entry.
(function () {
  'use strict';

  // Height of the contact point as a fraction of the figure's box: 0 is the top of the ink, 1 is
  // the floor it stands on. Authored, for the reason in the header. A pose that lands on nobody
  // (footwork, read, recover) still gets a value rather than a special case, because those beats
  // carry no impact stamp and never reach this code -- a null here would be an unreachable branch
  // pretending to be a decision.
  var STRIKE_Y = {
    headbutt: 0.16, kick_axe: 0.19, elbow: 0.27, punch_hook: 0.28, punch_straight: 0.30,
    punch_upper: 0.34, counter: 0.35, feint: 0.35, read: 0.35, kick_round: 0.38, frame: 0.38,
    guard: 0.40, rotation: 0.42, clinch: 0.45, kick_side: 0.45, pressure: 0.45, recover: 0.45,
    trap: 0.45, kick_front: 0.50, knee: 0.52, dirty: 0.55, throw: 0.55, takedown: 0.70,
    trip: 0.85, sweep: 0.88, footwork: 0.75
  };

  // Particles per heat, on the ladder the rest of the layer already uses. Quiet gets none: a graze
  // that produced a burst would be the loudest quiet beat in the game.
  //
  // Counts, radii and speeds were RAISED 2026-09-04 for the real beat panel (~790x455). The first
  // set was tuned on a purpose-built harness whose panels were 520x250, and none of it survived the
  // move -- ten particles of one-to-three pixels, thrown a few pixels apart, are invisible on a
  // surface twice the size. See the ladder comment in styles.css for the full correction.
  var SPAWN = { quiet: 0, solid: 10, heavy: 18, catastrophic: 28, finish: 34 };

  function reducedMotion() {
    try {
      return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
    } catch (e) { return false; }   // a browser with no matchMedia is not a browser asking for less
  }

  // How many particles this beat is worth. An absorbed blow gets a token few -- something was
  // touched -- and a whiff gets none at all, because nothing was.
  function spawnFor(heat, kind) {
    if (kind === 'whiff') return 0;
    var n = SPAWN[heat];
    if (n == null) n = SPAWN.solid;
    return kind === 'absorbed' ? Math.min(5, Math.round(n * 0.4) || 3) : n;
  }

  // The contact point in panel pixels. `rect` is the figure's own box, not the panel's: the figure
  // is centred and masked inside the backdrop, so anchoring to the panel would put a head kick's
  // burst in the corner on a wide screen.
  function originFor(pose, rect, facing) {
    var geo = (window.MOVE_ART_GEOMETRY && window.MOVE_ART_GEOMETRY[pose]) || null;
    var y = STRIKE_Y[pose];
    if (y == null) y = 0.4;
    // 1 - rx is the ink's right edge as a fraction of the box. Mirrored for a fighter drawn facing
    // left, so the burst is at the end of the technique rather than behind the shoulder.
    var edge = geo ? (1 - geo.rx) : 0.86;
    var fx = facing === 'left' ? (1 - edge) : edge;
    return {
      x: rect.x + rect.w * fx,
      y: rect.y + rect.h * y
    };
  }

  // One particle's state. Kept as a flat object rather than a class: this allocates once per
  // particle per beat and never in a loop that runs at frame rate.
  function makeParticle(ox, oy, speed, rand) {
    var a = rand() * Math.PI * 2;
    var v = speed * (0.35 + rand() * 0.65);
    return {
      x: ox, y: oy,
      vx: Math.cos(a) * v,
      vy: Math.sin(a) * v * 0.7,      // flatter than round: a blow throws sideways more than up
      life: 0,
      span: 260 + rand() * 240,       // ms, all inside the shortest dwell this can fire on
      r: 1.4 + rand() * 3.2
    };
  }

  function canvasFor(panel) {
    var c = panel.querySelector('canvas.impact-burst');
    if (c) return c;
    c = document.createElement('canvas');
    c.className = 'impact-burst';
    c.setAttribute('aria-hidden', 'true');
    panel.appendChild(c);
    return c;
  }

  // Fire one burst over `panel`. Everything about it is decided here and nothing is remembered:
  // a second call replaces the first rather than layering, because two bursts on one beat would
  // mean the beat resolved twice.
  function burst(panel, opts) {
    if (!panel || !opts) return null;
    if (reducedMotion()) return null;
    var count = spawnFor(opts.heat, opts.kind);
    if (!count) return null;

    var figure = panel.querySelector('.beat-backdrop img');
    var pr = panel.getBoundingClientRect();
    if (!pr.width || !pr.height) return null;
    var fr = figure ? figure.getBoundingClientRect() : pr;
    var rect = { x: fr.left - pr.left, y: fr.top - pr.top, w: fr.width, h: fr.height };
    var origin = originFor(opts.pose, rect, opts.facing);

    var c = canvasFor(panel);
    var dpr = Math.min(2, window.devicePixelRatio || 1);
    c.width = Math.round(pr.width * dpr);
    c.height = Math.round(pr.height * dpr);
    c.style.width = pr.width + 'px';
    c.style.height = pr.height + 'px';
    var ctx = c.getContext('2d');
    if (!ctx) return null;
    ctx.scale(dpr, dpr);

    var rand = opts.random || Math.random;
    var speed = opts.kind === 'absorbed' ? 0.09 : 0.19;
    var parts = [];
    for (var i = 0; i < count; i++) parts.push(makeParticle(origin.x, origin.y, speed, rand));

    var colour = opts.colour || '#ff5b62';
    var last = null;
    var raf = window.requestAnimationFrame;
    if (!raf) return null;

    function frame(now) {
      if (last === null) last = now;
      var dt = Math.min(48, now - last);   // a backgrounded tab returns a huge delta; clamp it
      last = now;
      ctx.clearRect(0, 0, c.width, c.height);
      var alive = 0;
      for (var j = 0; j < parts.length; j++) {
        var p = parts[j];
        p.life += dt;
        if (p.life >= p.span) continue;
        alive++;
        p.x += p.vx * dt;
        p.y += p.vy * dt + 0.00018 * dt * dt;   // a little gravity, so it falls rather than floats
        var t = 1 - (p.life / p.span);
        ctx.globalAlpha = t * t;
        ctx.fillStyle = colour;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r * (0.4 + t * 0.6), 0, Math.PI * 2);
        ctx.fill();
      }
      if (alive) { raf(frame); return; }
      // LAST FRAME CLEARS AND STOPS. Not "runs one more time to be safe" -- an idle rAF is the one
      // thing this file is not allowed to leave behind.
      ctx.clearRect(0, 0, c.width, c.height);
      if (c.parentNode) c.parentNode.removeChild(c);
    }
    raf(frame);
    return { count: count, origin: origin };
  }

  window.ImpactCanvas = {
    burst: burst,
    // Exported for tests/impact-canvas-test.js, which asserts the maths without a browser: the
    // spawn ladder, the derived origin, and that reduced motion is an early return rather than a
    // faster animation.
    spawnFor: spawnFor,
    originFor: originFor,
    reducedMotion: reducedMotion,
    STRIKE_Y: STRIKE_Y,
    SPAWN: SPAWN
  };
})();
